import CheckIcon from '@mui/icons-material/Check';
import DirectionsBoatFilledIcon from '@mui/icons-material/DirectionsBoatFilled';
import { FormControlLabel, ListItemButton, ListItemIcon, ListItemText, Stack, Switch, Typography } from '@mui/material';
import { AbsDropDown } from 'customer_portal-ui-shared';
import { getYear } from 'date-fns';
import { useEffect, useState } from 'react';
import { AbsColor } from '../../constants';
import { ICalendarRiskGroup } from '../../contracts/calendar-risk-group.contract';
import { CalendarAction, CalendarFetch } from '../../features/calendar.feature';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { Loading } from '../shared';
import CalendarRiskListItem from './calendar-risk-list-item.component';
import { ShowGuidelinesActions } from '../../features/showGuidelines.feature';

const CriteriaFilterStyle = {
    IconStyle: {
        color: AbsColor.White1
    },
    MenuStyle: {
        backgroundColor: AbsColor.Blue1,
        color: AbsColor.White1,
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
        width: '100%'
    }
};

const CalendarRiskList = () => {
    const dispatch = useAppDispatch();

    const selectedVessels = useAppSelector((state) => state.Calendar.Filters.Vessels);
    const riskRegulations = useAppSelector((state) => state.Calendar.Risk.List);
    const isLoading = useAppSelector((state) => state.Calendar.Risk.IsLoading);
    const showGuidelines = useAppSelector((state) => state.ShowGuidelines.showGuideLines);

    const [regulations, setRegulations] = useState<ICalendarRiskGroup[]>([]);
    const [selectedYear, setSelectedYear] = useState<string | undefined>(undefined);
    const [years, setYears] = useState<string[] | undefined>(undefined);

    useEffect(() => {
        if (!selectedVessels || selectedVessels.length === 0) {
            dispatch(CalendarAction.ClearRisks());
            return;
        }

        dispatch(CalendarFetch.GetRiskByClassNumberYear({ classNumbers: selectedVessels.flatMap((f) => f.ClassNumber), maxYear: getYear(new Date()) + 4 }));
    }, [selectedVessels, dispatch]);

    useEffect(() => {
        if (!riskRegulations || riskRegulations.length <= 0) {
            setRegulations([]);
            return;
        }

        if (!years || years.length <= 0) {
            const tempYears = riskRegulations.flatMap((f) => f.Year) ?? [];
            setYears(tempYears);
            if (!selectedYear) setSelectedYear(tempYears && tempYears.length > 0 ? tempYears[0] : undefined);
        }

        const tempRegulations: ICalendarRiskGroup[] =
            riskRegulations.flatMap((risk) => {
                return {
                    Year: risk.Year,
                    HighRisks: showGuidelines ? risk.HighRisks : risk.HighRisks.filter((f) => !f.Regulation.RegulationName.toLowerCase().includes('guideline')),
                    MediumRisks: showGuidelines ? risk.MediumRisks : risk.MediumRisks.filter((f) => !f.Regulation.RegulationName.toLowerCase().includes('guideline')),
                    LowRisks: showGuidelines ? risk.LowRisks : risk.LowRisks.filter((f) => !f.Regulation.RegulationName.toLowerCase().includes('guideline'))
                };
            }) ?? [];

        setRegulations([...tempRegulations]);
    }, [riskRegulations, showGuidelines, selectedYear]);

    const getHeaderTitle = (): string => {
        if (selectedVessels && selectedVessels.length > 0) {
            return `${selectedVessels.length} Vessel${selectedVessels.length > 1 ? 's' : ''} Selected`;
        }

        return `All Vessels Selected`;
    };

    return (
        <Stack direction={'column'} style={{ width: '100%' }} alignItems={'center'} justifyContent={'space-between'}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1} style={{ width: '100%', height: '50px' }}>
                <Stack direction={'row'}>
                    <DirectionsBoatFilledIcon style={{ color: AbsColor.Blue1 }} />
                    <div>{getHeaderTitle()}</div>
                </Stack>
                <Stack direction={'row'} alignItems={'center'} gap={5}>
                    {years && (
                        <Stack direction={'row'} alignItems={'center'} gap={1}>
                            <Typography>Year:</Typography>
                            <AbsDropDown
                                selected={selectedYear}
                                handleChange={(e: { label: string; value: string }) => setSelectedYear(e.value)}
                                options={years.map((m) => {
                                    return {
                                        label: m,
                                        value: m
                                    };
                                })}
                            />
                        </Stack>
                    )}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showGuidelines}
                                onChange={(e) => {
                                    dispatch(ShowGuidelinesActions.toggleShowGuidelines());
                                }}
                            />
                        }
                        label="Show Guidelines"
                    />
                </Stack>
            </Stack>
            <ListItemButton style={{ ...CriteriaFilterStyle.MenuStyle }}>
                <ListItemIcon>
                    <CheckIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                </ListItemIcon>
                <ListItemText primary={`Showing Regulations` + `${showGuidelines ? ' and Guidelines' : ''} in Year ${selectedYear}`} />
            </ListItemButton>

            {isLoading ? (
                <Stack style={{ padding: 5 }}>
                    <Loading />
                </Stack>
            ) : (
                <>
                    {regulations
                        .filter((f) => f.Year === selectedYear)
                        .map((risks) => (
                            <CalendarRiskListItem risks={risks} />
                        ))}
                </>
            )}
        </Stack>
    );
};

export default CalendarRiskList;
